import { openaiService } from '../openaiService';
import { 
  Message, 
  ChatResponse,
  PlaylistData
} from './types';
import {
  API_KEY,
  API_URL,
  MODEL,
  log,
  SYSTEM_PROMPT,
  extractPlaylistFromResponse,
  cleanResponseText
} from './utils';

export class AIService {
  private messages: Message[] = [];

  constructor() {
    // Initialize with system prompt
    this.resetConversation();
    
    // Log initialization
    log('AIService initialized with:');
    log(`API Key: ${API_KEY ? API_KEY.substring(0, 5) + '...' : 'Not set'}`);
    log(`API URL: ${API_URL}`);
    log(`Model: ${MODEL}`);
  }

  // Reset the conversation
  public resetConversation(): void {
    this.messages = [
      {
        role: 'system',
        content: SYSTEM_PROMPT,
      },
    ];
  }

  // Send a message to the AI and get a response
  public async sendMessage(message: string): Promise<ChatResponse> {
    log(`Sending message: ${message.substring(0, 50)}${message.length > 50 ? '...' : ''}`);
    
    try {
      // Add user message to conversation
      this.messages.push({
        role: 'user',
        content: message,
      });

      // Use the OpenAI client to send the message
      const response = await openaiService.sendMessage(
        this.messages.map(msg => ({
          role: msg.role,
          content: msg.content
        })),
        'none' // Don't use function calling for regular chat
      );
      
      // Extract assistant message
      const assistantMessage: Message = {
        role: 'assistant',
        content: response.choices[0].message.content || '',
      };

      log('Received response:', {
        content: assistantMessage.content.substring(0, 50) + 
                (assistantMessage.content.length > 50 ? '...' : '')
      });

      // Add to conversation history
      this.messages.push(assistantMessage);

      return {
        message: assistantMessage,
      };
    } catch (error: any) {
      log('Error sending message to AI:', error);
      
      // Enhanced error logging
      if (error.response) {
        log('Error response data:', error.response.data);
        log('Error response status:', error.response.status);
      } else {
        log('Error message:', error.message);
      }
      
      throw error;
    }
  }

  // Get music recommendations based on preferences
  public async getMusicRecommendations(preferences: {
    genres?: string[];
    artists?: string[];
    mood?: string;
    activity?: string;
    description?: string;
  }): Promise<ChatResponse> {
    const prompt = this.buildRecommendationPrompt(preferences);
    return this.sendMessage(prompt);
  }

  // Build a prompt for music recommendations
  private buildRecommendationPrompt(preferences: {
    genres?: string[];
    artists?: string[];
    mood?: string;
    activity?: string;
    description?: string;
  }): string {
    let prompt = 'I\'m looking for music recommendations';

    if (preferences.genres && preferences.genres.length > 0) {
      prompt += ` in the ${preferences.genres.join(', ')} genre(s)`;
    }

    if (preferences.artists && preferences.artists.length > 0) {
      prompt += ` similar to ${preferences.artists.join(', ')}`;
    }

    if (preferences.mood) {
      prompt += ` with a ${preferences.mood} mood`;
    }

    if (preferences.activity) {
      prompt += ` for ${preferences.activity}`;
    }

    if (preferences.description) {
      prompt += `. ${preferences.description}`;
    }

    prompt += '. Please suggest specific songs and artists that I might enjoy.';

    return prompt;
  }

  // Get the conversation history
  public getConversationHistory(): Message[] {
    // Return a copy of the messages array without the system prompt
    return [...this.messages].filter(msg => msg.role !== 'system');
  }
  
  // Public methods to expose utility functions
  public extractPlaylistFromResponse(response: string): PlaylistData | null {
    return extractPlaylistFromResponse(response);
  }
  
  public cleanResponseText(response: string): string {
    return cleanResponseText(response);
  }
}
